import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import { useChat } from '../../context/useChat'
import { useAuth } from '../../context/useAuth'
import { useWorkspace } from '../../context/useWorkspace'
import { useChatControls } from '../../hooks/useChatControls'
import { roleCanPerform } from '../../utils/workspacePermissions'
import { useToast } from '../../context/useToast'
import { useNls } from '../../nls/nlsProvider'
import { useTimeline } from '../../context/useTimeline'
import type { Channel, ChannelRole, ChannelType } from '../../types/chat'
import type { CreateChannelDraft } from '../../context/chatContextCore'
import { Checkbox, LinkedGoalPicker, TextInput, useLinkedGoalPrefill } from '../Common'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'
import styles from './chat.module.css'

interface CreateChannelModalProps {
  isOpen: boolean
  onClose: () => void
  onCreated?: (channelId: string) => void
  initialDraft?: CreateChannelDraft | null
  editChannel?: Channel | null
}

const SLUG_REGEX = /^[a-z0-9-]{1,32}$/

const slugify = (input: string): string =>
  input
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32)

const initialsOf = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

export const CreateChannelModal = ({ isOpen, onClose, onCreated, initialDraft, editChannel }: CreateChannelModalProps) => {
  const { createChannel, renameChannel, updateChannelMembers, members } = useChat()
  const { currentUser } = useAuth()
  const { activeWorkspaceId, activeRole, canPerform } = useWorkspace()
  const { canCreateChannel, canCreateDm } = useChatControls({ workspaceId: activeWorkspaceId })
  const { showToast } = useToast()
  const { t } = useNls()

  const canCreatePublic = canPerform('chat.create_channel')
  const canCreatePrivate = canPerform('chat.create_private_channel')
  const { elements } = useTimeline()
  const prefillGoalId = useLinkedGoalPrefill()

  const [name, setName] = useState('')
  const [type, setType] = useState<ChannelType>('public')
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set())
  const [memberRoles, setMemberRoles] = useState<Record<string, ChannelRole>>({})
  const [scopeNodeId, setScopeNodeId] = useState<string | null>(prefillGoalId)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isEditMode = Boolean(editChannel)
  const canManageChannel = roleCanPerform(activeRole, 'EDITOR')
  const closeIfIdle = () => {
    if (!isSubmitting) onClose()
  }

  useEffect(() => {
    if (isOpen) {
      setName(editChannel?.name ?? initialDraft?.name ?? '')
      setType(editChannel?.type ?? initialDraft?.type ?? 'public')
      setSelectedMemberIds(new Set(editChannel ? Object.keys(editChannel.roles) : initialDraft?.initialMemberIds ?? []))
      setMemberRoles(editChannel?.roles ?? {})
      setScopeNodeId(editChannel ? (editChannel.scopeNodeId ?? null) : (initialDraft?.scopeNodeId ?? prefillGoalId))
    } else {
      setName('')
      setType('public')
      setSelectedMemberIds(new Set())
      setMemberRoles({})
      setScopeNodeId(null)
    }
    setError(null)
    setIsSubmitting(false)
  }, [editChannel, initialDraft, isOpen, prefillGoalId])

  const slug = useMemo(() => slugify(name), [name])
  const slugIsValid = slug.length > 0 && SLUG_REGEX.test(slug)
  const trimmedName = name.trim()
  const nameTooLong = trimmedName.length > 64

  const eligibleMembers = useMemo(
    () => members.filter((m) => m.uid && m.uid !== currentUser?.uid),
    [members, currentUser?.uid],
  )
  const allMembers = useMemo(() => members.filter((m) => m.uid), [members])

  if (!isOpen) return null

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    if (!trimmedName) {
      setError(t('NLSChatValidationNameRequired'))
      return
    }
    if (nameTooLong) {
      setError(t('NLSChatValidationNameTooLong'))
      return
    }
    if (!slugIsValid) {
      setError(t('NLSChatValidationNameLetters'))
      return
    }
    if (isEditMode && !canManageChannel) {
      setError(t('NLSChatValidationOnlyAdminEditorEdit'))
      return
    }
    if (!isEditMode && type === 'private' && !canCreatePrivate) {
      setError(t('NLSChatValidationPrivateCreateDenied'))
      return
    }
    if (!isEditMode && type === 'public' && !canCreatePublic) {
      setError(t('NLSChatValidationPublicCreateDenied'))
      return
    }
    if (!isEditMode && !canCreateChannel) {
      setError(t('NLSControlNoticeChatOff'))
      return
    }
    if (!isEditMode && type === 'private' && selectedMemberIds.size === 1 && !canCreateDm) {
      setError(t('NLSControlNoticeChatNoDm'))
      return
    }
    if (isEditMode && type === 'private' && !Object.values(memberRoles).some((role) => role === 'admin')) {
      setError(t('NLSChatValidationPrivateNeedsOwner'))
      return
    }
    setIsSubmitting(true)
    try {
      if (editChannel) {
        if (trimmedName !== editChannel.name) await renameChannel(editChannel.id, trimmedName)
        const nextRoles = type === 'private' ? memberRoles : editChannel.roles
        const currentRoles = editChannel.roles
        const addMemberIds = Object.keys(nextRoles).filter((uid) => !currentRoles[uid])
        const removeMemberIds = Object.keys(currentRoles).filter((uid) => !nextRoles[uid])
        const promoteAdminIds = Object.entries(nextRoles).filter(([uid, role]) => currentRoles[uid] === 'member' && role === 'admin').map(([uid]) => uid)
        const demoteAdminIds = Object.entries(nextRoles).filter(([uid, role]) => currentRoles[uid] === 'admin' && role === 'member').map(([uid]) => uid)
        if (addMemberIds.length || removeMemberIds.length || promoteAdminIds.length || demoteAdminIds.length) {
          await updateChannelMembers(editChannel.id, { addMemberIds, removeMemberIds, promoteAdminIds, demoteAdminIds })
        }
        showToast(t('NLSChatToastChannelUpdated', { channel: slug }), 'success')
      } else {
        const channelId = await createChannel({
          name: trimmedName,
          type,
          initialMemberIds: type === 'private' ? Array.from(selectedMemberIds) : [],
          scopeNodeId,
        })
        showToast(t('NLSChatToastChannelCreated', { channel: slug }), 'success')
        onCreated?.(channelId)
      }
      onClose()
    } catch (err) {
      const message = err instanceof Error ? err.message : t('NLSChatErrorCreateChannelFailed')
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const toggleMember = (uid: string) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      return next
    })
    setMemberRoles((current) => {
      const next = { ...current }
      if (next[uid]) delete next[uid]
      else next[uid] = 'member'
      return next
    })
  }

  const setChannelRole = (uid: string, role: ChannelRole) => {
    setMemberRoles((current) => ({ ...current, [uid]: role }))
    setSelectedMemberIds((current) => new Set([...current, uid]))
  }

  // Portal the modal to <body> so it escapes stacking contexts introduced by
  // ancestors (RailPageShell uses `isolation: isolate`; some parents apply
  // `transform`/`backdrop-filter` that would otherwise trap `position: fixed`
  // and prevent the modal card from rendering above the rail).
  return createPortal(
    <div className={styles.modalBackdrop} onClick={closeIfIdle} role="presentation">
      <motion.form
        className={styles.modalCard}
        initial={{ y: 12, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 12, opacity: 0 }}
        transition={{ duration: 0.18, ease: 'easeOut' }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div>
          <h2 className={styles.modalTitle}>{isEditMode ? t('NLSChatTitleEditChannel') : t('NLSChatTitleCreateChannel')}</h2>
          <p className={styles.modalSubtitle}>
            {isEditMode ? t('NLSChatSubtitleEditChannel') : t('NLSChatSubtitleCreateChannel')}
          </p>
        </div>

        <div className={styles.field}>
          <label htmlFor="channel-name-input" className={styles.fieldLabel}>{t('NLSChatFieldChannelName')}</label>
          <TextInput
            id="channel-name-input"
            value={name}
            onChange={setName}
            placeholder={t('NLSChatPlaceholderDesignReviews')}
            maxLength={64}
            autoFocus
            error={Boolean(error && !slugIsValid)}
            disabled={isSubmitting}
          />
          {slug && (
            <span className={styles.slugPreview}>#{slug}</span>
          )}
        </div>

        {isEditMode ? (() => {
          const matchedGoal = editChannel?.scopeNodeId ? elements.find((el) => el.id === editChannel.scopeNodeId) : null
          return matchedGoal ? (
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t('NLSLinkedGoalLabel')}</span>
              <p className={styles.modalSubtitle}>
                {matchedGoal.title || matchedGoal.id}
              </p>
            </div>
          ) : null
        })() : (
          <div className={styles.field}>
            <LinkedGoalPicker
              value={scopeNodeId}
              onChange={setScopeNodeId}
              disabled={isSubmitting}
            />
          </div>
        )}

        <div className={styles.field}>
          <span className={styles.fieldLabel}>{t('NLSChatLabelPrivacy')}</span>
          <div className={styles.typeRow}>
            <Pressable
              className={`${styles.typeChip} ${type === 'public' ? styles.typeChipActive : ''}`}
              onClick={() => setType('public')}
              disabled={isSubmitting || isEditMode || !canCreatePublic}
              title={canCreatePublic ? '' : t('NLSChatTitlePrivacyPublicDenied')}
              aria-pressed={type === 'public'}
            >
              <span className={styles.typeChipTitle}>
                <PublicOutlinedIcon style={{ fontSize: 16, verticalAlign: '-3px', marginRight: 6 }} />
                {t('NLSChatLabelPublic')}
              </span>
              <span className={styles.typeChipHint}>
                {t('NLSChatTextPublicHint')}
              </span>
            </Pressable>
            <Pressable
              className={`${styles.typeChip} ${type === 'private' ? styles.typeChipActive : ''}`}
              onClick={() => setType('private')}
              disabled={isSubmitting || isEditMode || !canCreatePrivate}
              title={canCreatePrivate ? '' : t('NLSChatTitlePrivacyPrivateDenied')}
              aria-pressed={type === 'private'}
            >
              <span className={styles.typeChipTitle}>
                <LockOutlinedIcon style={{ fontSize: 16, verticalAlign: '-3px', marginRight: 6 }} />
                {t('NLSChatLabelPrivate')}
              </span>
              <span className={styles.typeChipHint}>
                {t('NLSChatTextPrivateHint')}
              </span>
            </Pressable>
          </div>
        </div>

        {type === 'private' && (isEditMode ? allMembers.length > 0 : eligibleMembers.length > 0) && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>
              {isEditMode
                ? t('NLSChatLabelMembersOwners', { count: Object.keys(memberRoles).length })
                : t('NLSChatLabelInitialMembers', { count: selectedMemberIds.size })}
            </span>
            <div className={styles.memberPicker}>
              {(isEditMode ? allMembers : eligibleMembers).map((member) => {
                const isChecked = Boolean(memberRoles[member.uid]) || selectedMemberIds.has(member.uid)
                return (
                  <div key={member.uid} className={styles.memberRow}>
                    <Checkbox
                      checked={isChecked}
                      onChange={() => toggleMember(member.uid)}
                      aria-label={member.name}
                      disabled={isSubmitting || (isEditMode && member.uid === currentUser?.uid && memberRoles[member.uid] === 'admin')}
                    />
                    <span className={styles.memberAvatar}>
                      {member.avatarUrl
                        ? <img src={member.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : initialsOf(member.name)}
                    </span>
                    <span style={{ flex: 1 }}>{member.name}</span>
                    {isEditMode && isChecked ? (
                      <Pressable
                        className={styles.roleToggle}
                        onClick={() => setChannelRole(member.uid, memberRoles[member.uid] === 'admin' ? 'member' : 'admin')}
                        disabled={isSubmitting}
                        aria-pressed={memberRoles[member.uid] === 'admin'}
                      >
                        {memberRoles[member.uid] === 'admin' ? t('NLSChatLabelOwner') : t('NLSChatLabelMembers')}
                      </Pressable>
                    ) : null}
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {isEditMode && type === 'public' && (
          <div className={styles.field}>
            <span className={styles.fieldLabel}>{t('NLSChatLabelAccess')}</span>
            <p className={styles.modalSubtitle}>{t('NLSChatTextAccessPublic')}</p>
          </div>
        )}

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.modalActions}>
          <Button variant="tertiary" onClick={onClose} disabled={isSubmitting}>
            {t('NLSChatActionCancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || !slugIsValid || nameTooLong}
            loading={isSubmitting}
          >
            {isSubmitting ? (isEditMode ? t('NLSChatActionSaving') : t('NLSChatActionCreating')) : isEditMode ? t('NLSChatActionSaveChanges') : t('NLSChatActionCreateChannel')}
          </Button>
        </div>
      </motion.form>
    </div>,
    document.body,
  )
}
